import React from "react";
import { fmtMoney, fmtDate } from "@/lib/apiClient";
import { StatusBadge } from "@/components/Shared";
import { TableCell, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Trash, Printer, FilePdf, CaretDown, CaretRight, ArrowSquareOut, DotsThreeVertical, Copy, EnvelopeSimple } from "@phosphor-icons/react";
import { Attachments } from "@/components/Attachments";
import { SendInvoiceDialog, EInvoiceDialog } from "@/components/InvoiceActions";

/** Small pill showing whether the invoice was uploaded (Tally) or internal. */
function SourceBadge({ source }) {
  const tally = source === "tally";
  return (
    <span
      data-testid={`invoice-source-${tally ? "tally" : "internal"}`}
      className={
        "inline-flex items-center gap-1 px-1.5 py-0.5 text-[9.5px] uppercase tracking-[0.14em] font-semibold rounded-md " +
        (tally
          ? "bg-amber-50 text-amber-800 border border-amber-200"
          : "bg-slate-100 text-slate-600 border border-slate-200")
      }
    >
      {tally ? "Tally" : "Internal"}
    </span>
  );
}

/**
 * A single row in the invoices table, plus an expandable panel showing
 * GST breakdown and attachments when clicked.
 *
 * Design-Wave-1: quick actions fade in on row hover; a kebab (⋮) menu
 * exposes secondary actions (duplicate, send email, delete) on every row.
 */
export function InvoiceRow({ inv, company, expanded, onToggleExpand, onReload, onDownloadPdf, onPrint, onDelete }) {
  const isTally = inv.source === "tally";
  return (
    <React.Fragment>
      <TableRow data-testid={`invoice-row-${inv.id}`} className="row-hover-parent border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
        <TableCell className="w-8">
          <button
            onClick={onToggleExpand}
            data-testid={`expand-invoice-${inv.id}`}
            className="text-slate-400 hover:text-[#EA580C] p-1 rounded-md hover:bg-slate-100 transition-colors"
          >
            {expanded ? <CaretDown size={13} /> : <CaretRight size={13} />}
          </button>
        </TableCell>
        <TableCell className="font-mono-num font-medium text-[12.5px]">{inv.invoice_number}</TableCell>
        <TableCell><SourceBadge source={inv.source} /></TableCell>
        <TableCell className="text-[13px]">
          {inv.client_name}
          {inv.is_interstate && <span className="ml-2 text-[9px] uppercase tracking-[0.14em] font-semibold text-sky-700 bg-sky-50 px-1.5 py-0.5 rounded-md">IGST</span>}
        </TableCell>
        <TableCell className="text-slate-500 text-[12px]">{fmtDate(inv.issue_date)}</TableCell>
        <TableCell className="text-slate-500 text-[12px]">{fmtDate(inv.due_date)}</TableCell>
        <TableCell><StatusBadge status={inv.status} /></TableCell>
        <TableCell className="text-right font-mono-num text-[12.5px] text-slate-700">{fmtMoney(inv.subtotal || 0)}</TableCell>
        <TableCell className="text-right font-mono-num text-[12.5px] text-slate-600">{fmtMoney(inv.tax_amount || 0)}</TableCell>
        <TableCell className="text-right font-mono-num text-[13px]">{fmtMoney(inv.total)}</TableCell>
        <TableCell className="text-right font-mono-num text-emerald-700 text-[13px]">{fmtMoney(inv.amount_paid)}</TableCell>
        <TableCell className="text-right font-mono-num font-semibold text-[13px]">{fmtMoney(inv.total - inv.amount_paid)}</TableCell>
        <TableCell className="w-[140px]">
          <div className="flex items-center justify-end gap-0.5">
            {/* Quick actions — visible on row hover */}
            <div className="row-hover-actions flex items-center gap-0.5">
              {isTally ? (
                <button
                  onClick={() => onDownloadPdf(inv)}
                  data-testid={`view-tally-pdf-${inv.id}`}
                  title="View Tally PDF"
                  className="kebab-btn hover:text-[#EA580C]"
                >
                  <ArrowSquareOut size={14} />
                </button>
              ) : (
                <>
                  <SendInvoiceDialog invoice={inv} company={company} />
                  <button onClick={() => onDownloadPdf(inv)} data-testid={`download-invoice-pdf-${inv.id}`} title="Download PDF" className="kebab-btn hover:text-[#EA580C]"><FilePdf size={14} /></button>
                  <button onClick={() => onPrint(inv)} data-testid={`print-invoice-${inv.id}`} title="Print" className="kebab-btn hover:text-[#EA580C]"><Printer size={14} /></button>
                </>
              )}
            </div>

            {/* Kebab menu — always visible, secondary actions */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid={`row-actions-${inv.id}`}
                  className="kebab-btn"
                  title="More actions"
                  onClick={(e) => e.stopPropagation()}
                >
                  <DotsThreeVertical size={16} weight="bold" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={onToggleExpand} data-testid={`kebab-expand-${inv.id}`}>
                  <CaretDown size={13} className="mr-2" /> {expanded ? "Collapse" : "Show details"}
                </DropdownMenuItem>
                {!isTally && (
                  <DropdownMenuItem onClick={() => onDownloadPdf(inv)} data-testid={`kebab-download-${inv.id}`}>
                    <FilePdf size={13} className="mr-2" /> Download PDF
                  </DropdownMenuItem>
                )}
                {isTally && (
                  <DropdownMenuItem onClick={() => onDownloadPdf(inv)} data-testid={`kebab-view-tally-${inv.id}`}>
                    <ArrowSquareOut size={13} className="mr-2" /> Open Tally PDF
                  </DropdownMenuItem>
                )}
                {!isTally && (
                  <DropdownMenuItem onClick={() => onPrint(inv)} data-testid={`kebab-print-${inv.id}`}>
                    <Printer size={13} className="mr-2" /> Print
                  </DropdownMenuItem>
                )}
                <EInvoiceDialog invoice={inv} onGenerated={onReload} asDropdownItem />
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => onDelete(inv.id)}
                  data-testid={`delete-invoice-${inv.id}`}
                  className="text-red-600 focus:text-red-700 focus:bg-red-50"
                >
                  <Trash size={13} className="mr-2" /> Delete invoice
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </TableCell>
      </TableRow>

      {expanded && (
        <TableRow className="bg-slate-50/70 border-b border-slate-200">
          <TableCell colSpan={13} className="p-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="section-eyebrow mb-3">GST Breakdown</div>
                <div className="text-[13px] space-y-1 font-mono-num">
                  <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span>{fmtMoney(inv.subtotal)}</span></div>
                  {inv.is_interstate ? (
                    <div className="flex justify-between"><span className="text-slate-500">IGST</span><span>{fmtMoney(inv.igst_amount)}</span></div>
                  ) : (
                    <>
                      <div className="flex justify-between"><span className="text-slate-500">CGST</span><span>{fmtMoney(inv.cgst_amount)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">SGST</span><span>{fmtMoney(inv.sgst_amount)}</span></div>
                    </>
                  )}
                  <div className="flex justify-between font-semibold text-[#EA580C] border-t border-slate-200 pt-1.5 mt-1"><span>Total</span><span>{fmtMoney(inv.total)}</span></div>
                  {inv.client_gstin && <div className="flex justify-between text-slate-500 pt-1"><span>Client GSTIN</span><span>{inv.client_gstin}</span></div>}
                  {inv.place_of_supply && <div className="flex justify-between text-slate-500"><span>Place of Supply</span><span>{inv.place_of_supply}</span></div>}
                  {isTally && <div className="flex justify-between text-amber-800 pt-2 mt-2 border-t border-amber-200"><span className="uppercase tracking-widest text-[10px] font-semibold">Source</span><span>Tally · uploaded PDF</span></div>}
                </div>
              </div>
              <div>
                <Attachments parentType="invoice" parentId={inv.id} />
              </div>
            </div>
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  );
}
